/**
 * 互动游戏的"编辑器"（对应 iOS UploadFlowView 的 GameLibraryView / GameParamsView）：
 * 从游戏库选一个游戏 → 按它的 params_schema 填设置 → payload = { package: { id, version }, params }。
 * 服务端补全 asset_id、sha256 和默认值（docs/API.md 2.6.1）。
 */
import { ChevronRight, Gamepad2 } from "lucide-react";
import { useEffect, useState } from "react";
import { listGames, type WebPackage } from "../../activities/games";
import { type FormValues, mergeFormValues, parseFormSchema } from "../../activities/schema-form";
import { SchemaFields } from "../../activities/SchemaForm";
import { C, EmptyHint, FormCard, ModalHeader, Pill, SheetBody, Spinner } from "../../ui";
import type { EditorProps } from "./editors";
import { BackHeader } from "./Library";

export function GameEditor({ onCancel, onSave }: EditorProps) {
  const [games, setGames] = useState<WebPackage[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<WebPackage | null>(null);

  useEffect(() => {
    let alive = true;
    listGames()
      .then((l) => alive && setGames(l))
      .catch((e: unknown) => alive && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      alive = false;
    };
  }, []);

  if (picked) return <GameParams game={picked} onBack={() => setPicked(null)} onSave={onSave} />;

  return (
    <>
      <BackHeader title="游戏库" onBack={onCancel} />
      <SheetBody gap={10}>
        {error && <EmptyHint icon={Gamepad2} text={`游戏库加载失败：${error}`} />}
        {!error && !games && (
          <div className="learn-game-loading">
            <Spinner size={24} />
          </div>
        )}
        {games?.length === 0 && <EmptyHint icon={Gamepad2} text="游戏库里还没有游戏" />}
        {games?.map((g) => (
          <button key={g.id} type="button" className="learn-type-option" onClick={() => setPicked(g)} data-testid={`game-${g.id}`}>
            <GameIcon game={g} size={48} />
            <span className="learn-row-main">
              <strong>{g.name}</strong>
              <span>{g.description ?? g.id}</span>
              <span className="learn-game-tags">
                {g.tags.map((t) => (
                  <Pill key={t} size={11}>
                    {t}
                  </Pill>
                ))}
                {g.estimatedMin != null && <Pill size={11}>约 {g.estimatedMin} 分钟</Pill>}
              </span>
            </span>
            <ChevronRight size={16} strokeWidth={2.6} color={C.inkSoft} />
          </button>
        ))}
      </SheetBody>
    </>
  );
}

export function GameIcon({ game, size }: { game: Pick<WebPackage, "icon" | "name">; size: number }) {
  if (game.icon) return <img className="learn-game-icon" src={game.icon} alt="" width={size} height={size} />;
  return (
    <span className="learn-game-icon is-placeholder" style={{ width: size, height: size }}>
      <Gamepad2 size={size / 2} />
    </span>
  );
}

function GameParams({ game, onBack, onSave }: { game: WebPackage; onBack: () => void; onSave: EditorProps["onSave"] }) {
  const fields = parseFormSchema(game.paramsSchema);
  const [values, setValues] = useState<FormValues>({});
  const assignable = fields.filter((f) => f.assign).map((f) => f.title);
  const next = () => {
    const params = mergeFormValues(values, {}, fields);
    onSave({ package: { id: game.id, version: game.version }, params }, 1, { title: game.name, minutes: game.estimatedMin ?? undefined });
  };
  return (
    <>
      <ModalHeader title={game.name} cancelTitle="返回" actionTitle="下一步" actionEnabled onCancel={onBack} onAction={next} />
      <SheetBody gap={12}>
        <div className="learn-detail-head">
          <GameIcon game={game} size={56} />
          <div>
            <h2>{game.name}</h2>
            <p>{[game.description, `版本 ${game.version}`].filter(Boolean).join(" · ")}</p>
          </div>
        </div>
        {fields.length > 0 ? (
          <FormCard>
            <SchemaFields fields={fields} values={values} onChange={setValues} />
          </FormCard>
        ) : (
          <p className="learn-note">这个游戏没有可以设置的参数。</p>
        )}
        {assignable.length > 0 && <p className="learn-note">布置时还可以再改「{assignable.join("、")}」。</p>}
      </SheetBody>
    </>
  );
}
